// The fee lines of an OPD bill, priced from the hospital's OPD fee schedule
// (Admin → Billing & Insurance → OPD Fee Schedule). Shared by the visit
// pipeline's Billing stage and the Billing screen's "New OPD bill", so both
// fetch the same fees and apply the same rules.
//
// What a biller may change is decided by billing:fee-override (quote.can_override):
//   with it    — edit any fee and GST rate, any discount, add unscheduled charges
//   without it — fees and GST are fixed; a discount up to the item's limit only
// The server checks the same thing on save (POST /api/billing/opd/create), so
// this screen is a convenience, not the control.
import { useEffect, useMemo, useState } from 'react';
import { Lock, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { useOpdFeeQuote, type OpdFeeQuoteParams } from './api';
import { CATEGORY_LABELS, type OpdFeeQuote, type OpdFeeQuoteItem } from './types';
import { initialLines, lineFromItem, nextKey, type OpdLine } from './opdLines';

export type { OpdLine, OpdBillItem } from './opdLines';
export { initialLines, toBillItems, billTotals, lineProblems } from './opdLines';

/**
 * Loads the schedule for this bill and holds its lines. The lines are reset
 * whenever the priced context changes (another patient, department or branch).
 */
export function useOpdBillLines(params: OpdFeeQuoteParams, enabled = true) {
  const quoteQuery = useOpdFeeQuote(params, enabled);
  const quote = quoteQuery.data;
  const [lines, setLines] = useState<OpdLine[]>([]);
  useEffect(() => { setLines(initialLines(quote)); }, [quote]);
  return {
    quoteQuery,
    quote,
    lines,
    setLines,
    reset: () => setLines(initialLines(quote)),
  };
}

export function OpdFeeLinesEditor({
  quote, lines, onChange, gstEnabled, gstOptions,
}: {
  quote: OpdFeeQuote;
  lines: OpdLine[];
  onChange: (lines: OpdLine[]) => void;
  gstEnabled: boolean;
  gstOptions: number[];
}) {
  const canOverride = quote.can_override;
  const byId = useMemo(() => new Map(quote.items.map((i) => [i.id, i])), [quote.items]);
  const unused = quote.items.filter((i) => !lines.some((l) => l.itemId === i.id));
  const update = (key: string, patch: Partial<OpdLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => onChange(lines.filter((l) => l.key !== key));
  const add = (item: OpdFeeQuoteItem) => onChange([...lines, lineFromItem(item)]);
  const addOther = () => onChange([...lines, { key: nextKey(), itemId: null, category: null, description: '', fee: '', discount: '0', gst: gstOptions[0] ?? 0 }]);

  const cols = gstEnabled ? 'grid-cols-[minmax(0,1fr)_96px_72px_76px_32px]' : 'grid-cols-[minmax(0,1fr)_96px_72px_32px]';

  return (
    <div>
      {lines.length > 0 && (
        <div className={cn('mb-1 grid gap-2 px-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted', cols)}>
          <span>Item</span><span>Fee (₹)</span><span>Disc. %</span>{gstEnabled && <span>GST %</span>}<span />
        </div>
      )}
      {lines.map((l) => {
        const item = l.itemId != null ? byId.get(l.itemId) : undefined;
        const discountLimit = canOverride ? 100 : item?.max_discount_percent ?? 0;
        const overLimit = (Number(l.discount) || 0) > discountLimit;
        const gstChoices = item && !gstOptions.includes(l.gst) ? [...gstOptions, l.gst].sort((a, b) => a - b) : gstOptions;
        return (
          <div key={l.key} className={cn('mb-2 grid items-start gap-2', cols)}>
            <div className="min-w-0">
              {item ? (
                <div className="flex min-h-9 flex-col justify-center">
                  <span className="truncate text-[13px] font-semibold">{item.name}</span>
                  <span className="text-[10.5px] text-ink-muted">
                    {CATEGORY_LABELS[item.category]}
                    {item.fee_source !== 'default' && ' · fee for this ' + (item.fee_source === 'branch' ? 'branch' : 'department')}
                  </span>
                </div>
              ) : (
                <Input aria-label="Charge name" placeholder="Charge name, e.g. Dressing" value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} />
              )}
            </div>
            <div>
              {canOverride || !item ? (
                <Input aria-label={`Fee for ${l.description || 'charge'}`} type="number" min="0" inputMode="decimal" value={l.fee} onChange={(e) => update(l.key, { fee: e.target.value })} />
              ) : (
                <div className="flex min-h-9 items-center gap-1 rounded-(--radius) bg-canvas px-2 text-[13px] font-semibold tabular-nums" title="Set in the OPD fee schedule">
                  <Lock className="h-3 w-3 text-ink-muted" />{Number(l.fee).toFixed(2)}
                </div>
              )}
              {item && canOverride && Number(l.fee) !== item.fee && (
                <div className="mt-0.5 text-[10.5px] text-warn-ink">Schedule: ₹{item.fee.toFixed(2)}</div>
              )}
            </div>
            <div>
              <Input
                aria-label={`Discount for ${l.description || 'charge'}`}
                type="number" min="0" max={discountLimit} inputMode="decimal"
                value={l.discount}
                disabled={!canOverride && discountLimit === 0}
                title={!canOverride && discountLimit === 0 ? 'No discount allowed on this item' : undefined}
                onChange={(e) => update(l.key, { discount: e.target.value })}
                invalid={overLimit}
              />
              {overLimit && <div className="mt-0.5 text-[10.5px] text-danger-ink">Max {discountLimit}%</div>}
            </div>
            {gstEnabled && (
              canOverride || !item ? (
                <NativeSelect aria-label="GST %" value={l.gst} onChange={(e) => update(l.key, { gst: Number(e.target.value) })}>
                  {gstChoices.map((g) => <option key={g} value={g}>{g}%</option>)}
                </NativeSelect>
              ) : (
                <div className="flex min-h-9 items-center text-[13px] tabular-nums">{l.gst}%</div>
              )
            )}
            <Button variant="ghost" size="icon" aria-label={`Remove ${l.description || 'charge'}`} onClick={() => remove(l.key)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      {lines.length === 0 && (
        <div className="mb-2 rounded-(--radius) border border-dashed border-line px-3 py-3 text-center text-[12px] text-ink-muted">
          No charges yet — add one below.
        </div>
      )}

      <div className="mt-1 flex flex-wrap gap-1.5">
        {unused.map((i) => (
          <Button key={i.id} variant="ghost" size="sm" onClick={() => add(i)}>
            <Plus className="h-3.5 w-3.5" /> {i.name} <span className="text-ink-muted">₹{i.fee.toFixed(0)}</span>
          </Button>
        ))}
        <Button
          variant="ghost" size="sm" onClick={addOther} disabled={!canOverride}
          title={canOverride ? 'A charge not on the fee schedule' : 'Your role can only bill items on the fee schedule'}
        >
          <Plus className="h-3.5 w-3.5" /> Other charge
        </Button>
      </div>
      {!canOverride && (
        <div className="mt-1.5 text-[11px] text-ink-muted">
          Fees come from the hospital&apos;s OPD fee schedule. Your role can give a discount up to each item&apos;s limit.
        </div>
      )}
    </div>
  );
}
